import { isDefined, isNil } from "@clipboard-health/util-ts";
import { parseISO } from "date-fns";

import { type BlockingRequirementsContext } from "../Bookability/Documents/types";

interface UseBlockingRequirementsPillsProps {
  blockingRequirementsContext?: BlockingRequirementsContext;
  hasPaymentsDisabled: boolean;
  hasAgreementToSign: boolean;
}

export function useBlockingRequirementsPills(props: UseBlockingRequirementsPillsProps) {
  const { blockingRequirementsContext, hasPaymentsDisabled, hasAgreementToSign } = props;

  const {
    visibleBlockingRequirements,
    areAllVisibleBlockedDocumentsInstantReview,
    areAllVisibleBlockedDocumentsExpiringSoon,
  } = blockingRequirementsContext ?? {};

  function getBlockingRequirementsDisplayCount() {
    if (isNil(blockingRequirementsContext)) {
      return 0;
    }

    if (blockingRequirementsContext.notInReviewVisibleBlockingRequirements.length > 0) {
      return blockingRequirementsContext.notInReviewVisibleBlockingRequirements.length;
    }

    if (blockingRequirementsContext.inReviewVisibleBlockingRequirements.length > 0) {
      return blockingRequirementsContext.inReviewVisibleBlockingRequirements.length;
    }

    return blockingRequirementsContext.invisibleBlockingRequirements.length;
  }

  const originalBlockingRequirementsDisplayCount = getBlockingRequirementsDisplayCount();

  // if we have blocking requirements, payments and agreement are included
  // otherwise, they will have their own pills
  const blockingRequirementsDisplayCount =
    originalBlockingRequirementsDisplayCount > 0
      ? originalBlockingRequirementsDisplayCount +
        Number(hasPaymentsDisabled) +
        Number(hasAgreementToSign)
      : 0;

  // Don't show the display count if all visible blocking requirements are all instant review
  const showBlockingRequirementDisplayCount =
    blockingRequirementsDisplayCount > 0 &&
    (!areAllVisibleBlockedDocumentsInstantReview ||
      blockingRequirementsContext?.inReviewVisibleBlockingRequirements.length ===
        blockingRequirementsDisplayCount);

  return {
    showBlockingRequirementsPill: showBlockingRequirementDisplayCount,
    blockingRequirementsPillCount: blockingRequirementsDisplayCount,
    areBlockingRequirementsExpiringSoon:
      Boolean(areAllVisibleBlockedDocumentsExpiringSoon) &&
      !hasAgreementToSign &&
      !hasPaymentsDisabled,
    blockingRequirementExpiryDate:
      areAllVisibleBlockedDocumentsExpiringSoon &&
      blockingRequirementsDisplayCount === 1 &&
      isDefined(visibleBlockingRequirements?.[0]?.expiry)
        ? parseISO(visibleBlockingRequirements[0].expiry)
        : undefined,
  };
}
